import { useState } from "react";
import {
  HARDWARE_CATALOGUE,
  HARDWARE_PULLS,
  isPullHardware,
} from "../data/productSpecs";

/* ---------- HelpTip: small info button that toggles a hint block ---------- */
export function HelpTip({ label, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-flex flex-col">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#0a0a0c]/25 font-tech text-[10px] text-[#0a0a0c]/60 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
        aria-label={`Help — ${label}`}
      >
        ?
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-10 mt-6 rounded-sm border border-[#0a0a0c]/15 bg-white px-4 py-3 font-body text-[12px] leading-relaxed text-[#0a0a0c]/85 shadow-lg">
          <div className="mb-1 font-tech text-[10px] font-semibold uppercase tracking-[0.28em] text-[#c6a87c]">
            {label}
          </div>
          {children}
        </div>
      )}
    </div>
  );
}

export function LabelWithHelp({ label, help, testId }) {
  return (
    <div className="relative flex items-center gap-2">
      <span aria-hidden className="h-[10px] w-[3px] rounded-full bg-[#c6a87c]" />
      <span className="font-tech text-[10px] font-semibold uppercase tracking-[0.32em] text-[#0a0a0c]">
        {label}
      </span>
      <HelpTip label={label} testId={testId}>
        {help}
      </HelpTip>
    </div>
  );
}

/* ---------- Reusable picker grid ---------- */
function PickerGrid({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-3 gap-2 sm:grid-cols-4"
    >
      {children}
    </div>
  );
}

function PickerTile({ selected, onClick, label, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={selected}
      className={`group flex flex-col overflow-hidden rounded-sm border transition-all duration-300 ${
        selected
          ? "border-[#0a0a0c] shadow-[0_0_0_2px_#0a0a0c_inset]"
          : "border-[#0a0a0c]/15 hover:border-[#c6a87c]"
      }`}
    >
      <div className="relative aspect-square w-full bg-[#f6f4ef]">{children}</div>
      <span className="border-t border-[#0a0a0c]/8 bg-white px-2 py-1.5 text-center font-tech text-[9px] font-semibold uppercase tracking-[0.14em] text-[#0a0a0c]">
        {label}
      </span>
    </button>
  );
}

/* ---------- Glass Type Picker (visual swatches) ---------- */
const REEDED_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_90d22f1f-b078-4e8d-bee2-20170bd7c0ae/artifacts/wrxja72m_fluted%20glass%201.jpg";
const FLEMISH_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_90d22f1f-b078-4e8d-bee2-20170bd7c0ae/artifacts/gpmwlhpa_flemish%20glass%201.jpg";

const GLASS_VISUALS = {
  "Single Glazed": {
    // Subtle diagonal sheen, hint of blue — mimics a single clean sheet
    pattern:
      "linear-gradient(120deg,rgba(255,255,255,0.9) 0%,rgba(215,229,234,0.85) 45%,rgba(255,255,255,0.9) 100%),linear-gradient(180deg,#e4ebee 0%,#f2f5f6 100%)",
  },
  "Double Glazed": {
    // Two panes visible via parallel reflection stripes + inner shadow
    pattern:
      "linear-gradient(90deg,rgba(255,255,255,0.4) 0%,transparent 20%,transparent 40%,rgba(255,255,255,0.3) 45%,transparent 50%,transparent 80%,rgba(255,255,255,0.4) 100%),linear-gradient(180deg,#dae5ea 0%,#eaf1f3 55%,#dae5ea 100%)",
  },
  "Low-E DGU": {
    // Iridescent low-emissivity coating — cool blue/mauve tint
    pattern:
      "linear-gradient(135deg,rgba(156,186,205,0.65) 0%,rgba(206,204,229,0.55) 45%,rgba(178,206,221,0.7) 100%),linear-gradient(180deg,#c9d9e0 0%,#d8dfe6 100%)",
  },
  "Reeded (fluted)": { image: REEDED_IMG },
  Frosted: {
    // Diffused white with soft radial glow — reads as heavily blurred obscure glass
    pattern:
      "radial-gradient(ellipse at 30% 25%,rgba(255,255,255,0.95) 0%,rgba(230,238,240,0.9) 45%,rgba(210,222,226,0.85) 100%)",
  },
  Flemish: { image: FLEMISH_IMG },
};

export function GlassPicker({ value, onChange, options }) {
  return (
    <PickerGrid testId="quote-glass-picker">
      {options.map((g) => {
        const v = GLASS_VISUALS[g];
        return (
          <PickerTile
            key={g}
            selected={value === g}
            onClick={() => onChange(g)}
            label={g.replace(" (fluted)", "")}
            testId={`quote-glass-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {v?.image ? (
              <img
                src={v.image}
                alt={g}
                loading="lazy"
                className="absolute inset-1 h-[calc(100%-8px)] w-[calc(100%-8px)] rounded-sm object-cover"
              />
            ) : (
              <div
                className="absolute inset-1 rounded-sm"
                style={{ background: v?.pattern || "#e5edf0" }}
              />
            )}
          </PickerTile>
        );
      })}
    </PickerGrid>
  );
}

/* ---------- Hardware Picker (real images + generic tiles for pulls) ---------- */
const PULL_ICONS = {
  "Pull Handle": (
    <div className="absolute inset-x-6 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[#3a3a3d]" />
  ),
  "Half-Moon Pull": (
    <div className="absolute left-1/2 top-1/2 h-3/5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3a3a3d] before:absolute before:inset-x-[-8px] before:top-1/2 before:h-1.5 before:-translate-y-1/2 before:rounded-full before:bg-[#3a3a3d]" />
  ),
  "Rod Pull": (
    <div className="absolute left-1/2 top-1/2 h-4/5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3a3a3d]" />
  ),
  "Bespoke Pull": (
    <div className="absolute inset-x-4 top-1/2 h-1 -translate-y-1/2 rounded-full bg-[#c6a87c]" />
  ),
  "Full-Height Bar": (
    <div className="absolute left-1/2 inset-y-3 w-1.5 -translate-x-1/2 rounded-full bg-[#3a3a3d]" />
  ),
};

export function HardwarePicker({ catKey, value, onChange }) {
  const cat = HARDWARE_CATALOGUE[catKey] || [];
  const pulls = HARDWARE_PULLS[catKey] || [];
  return (
    <div className="flex flex-col gap-4">
      {cat.length > 0 && (
        <div>
          <p className="mb-2 font-tech text-[9px] font-semibold uppercase tracking-[0.28em] text-[#0a0a0c]/60">
            Hardware Catalogue
          </p>
          <PickerGrid testId="quote-hardware-catalogue">
            {cat.map((h) => (
              <PickerTile
                key={h.value}
                selected={value === h.value}
                onClick={() => onChange(h.value)}
                label={h.value}
                testId={`quote-hardware-${h.value.toLowerCase()}`}
              >
                <img
                  src={`/doors/${h.value.toLowerCase()}.webp`}
                  alt={h.label}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-contain p-2"
                />
              </PickerTile>
            ))}
          </PickerGrid>
        </div>
      )}
      {pulls.length > 0 && (
        <div>
          <p className="mb-2 font-tech text-[9px] font-semibold uppercase tracking-[0.28em] text-[#0a0a0c]/60">
            Pull Handles — size required
          </p>
          <PickerGrid testId="quote-hardware-pulls">
            {pulls.map((p) => (
              <PickerTile
                key={p}
                selected={value === p}
                onClick={() => onChange(p)}
                label={p}
                testId={`quote-hardware-${p.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {PULL_ICONS[p] || (
                  <div className="absolute inset-x-6 top-1/2 h-1 -translate-y-1/2 rounded-full bg-[#3a3a3d]" />
                )}
              </PickerTile>
            ))}
          </PickerGrid>
        </div>
      )}
    </div>
  );
}

export { isPullHardware };

/* ---------- Swing Direction Picker (4 architectural plan tiles) ---------- */
// Each tile shows a mini plan view: interior/exterior labels, walls with a
// gap for the opening, hinge dot, door leaf, and a dashed swing arc.
function SwingDiagram({ direction, hand }) {
  // Base geometry — walls at y=45, hinge on left at x=15 (mirrored for right).
  const flip = hand === "right";
  const outSwing = direction === "out";
  // Y-position of the door leaf & arc: in-swing goes up (interior side above wall),
  // out-swing goes down (exterior side below wall).
  return (
    <svg
      viewBox="0 0 100 80"
      className="absolute inset-2 h-[calc(100%-16px)] w-[calc(100%-16px)]"
      aria-hidden
    >
      <g style={flip ? { transform: "translateX(100px) scaleX(-1)" } : undefined}>
        {/* Labels sit outside the g so they don't flip — draw them separately */}
        {/* Left wall */}
        <rect x="4" y="42" width="26" height="6" fill="currentColor" opacity="0.85" />
        {/* Right wall */}
        <rect x="60" y="42" width="36" height="6" fill="currentColor" opacity="0.85" />
        {/* Opening (dotted) */}
        <line
          x1="30"
          y1="45"
          x2="60"
          y2="45"
          stroke="currentColor"
          strokeWidth="0.8"
          strokeDasharray="1.5 1.5"
          opacity="0.6"
        />
        {/* Hinge dot */}
        <circle cx="30" cy="45" r="2.4" fill="currentColor" />
        {outSwing ? (
          <>
            {/* Out-swing: door swings down (toward exterior) */}
            <line
              x1="30"
              y1="45"
              x2="30"
              y2="70"
              stroke="currentColor"
              strokeWidth="2.2"
            />
            <path
              d="M 30 70 A 25 25 0 0 0 55 45"
              stroke="currentColor"
              strokeWidth="0.9"
              fill="none"
            />
          </>
        ) : (
          <>
            {/* In-swing: door swings up (toward interior) */}
            <line
              x1="30"
              y1="45"
              x2="30"
              y2="20"
              stroke="currentColor"
              strokeWidth="2.2"
            />
            <path
              d="M 30 20 A 25 25 0 0 1 55 45"
              stroke="currentColor"
              strokeWidth="0.9"
              fill="none"
            />
          </>
        )}
      </g>
    </svg>
  );
}

const SWING_TILES = [
  { key: "In-swing · Left Hand", direction: "in", hand: "left", label: "IN-SWING LEFT" },
  { key: "In-swing · Right Hand", direction: "in", hand: "right", label: "IN-SWING RIGHT" },
  { key: "Out-swing · Left Hand", direction: "out", hand: "left", label: "OUT-SWING LEFT" },
  { key: "Out-swing · Right Hand", direction: "out", hand: "right", label: "OUT-SWING RIGHT" },
];

export function SwingPicker({ opening, handing, onChange }) {
  const currentKey = `${opening} · ${handing}`;
  return (
    <div className="flex flex-col gap-2">
      <div
        data-testid="quote-swing-picker"
        className="grid grid-cols-2 gap-2"
      >
        {SWING_TILES.map((t) => {
          const selected = t.key === currentKey;
          const dirLabel = t.direction === "in" ? "In-swing" : "Out-swing";
          const handLabel = t.hand === "left" ? "Left Hand" : "Right Hand";
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`quote-swing-${t.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onChange({ opening: dirLabel, handing: handLabel })}
              aria-pressed={selected}
              style={{
                animation: selected
                  ? "neon-pulse 2.6s ease-in-out infinite"
                  : "none",
                transition: "border-color 300ms ease-out",
              }}
              className={`group flex flex-col overflow-hidden rounded-sm border transition-colors duration-300 ${
                selected
                  ? "border-[#c6a87c] text-[#0a0a0c] bg-[#c6a87c]/8"
                  : "border-[#0a0a0c]/15 text-[#0a0a0c]/85 hover:border-[#c6a87c]/60"
              }`}
            >
              <div className="relative aspect-square w-full bg-[#f6f4ef]">
                {/* Interior / Exterior labels */}
                <span className="absolute inset-x-0 top-1.5 text-center font-tech text-[7px] font-semibold uppercase tracking-[0.2em] text-[#0a0a0c]/60">
                  Interior
                </span>
                <span className="absolute inset-x-0 bottom-1.5 text-center font-tech text-[7px] font-semibold uppercase tracking-[0.2em] text-[#0a0a0c]/60">
                  Exterior
                </span>
                <SwingDiagram direction={t.direction} hand={t.hand} />
              </div>
              <span className="border-t border-[#0a0a0c]/8 bg-white px-2 py-1.5 text-center font-tech text-[9px] font-semibold uppercase tracking-[0.14em]">
                {t.label}
              </span>
            </button>
          );
        })}
      </div>
      <p className="font-tech text-[9px] uppercase tracking-[0.24em] text-[#0a0a0c]/50">
        Left / Right = hinge side viewed from exterior
      </p>
    </div>
  );
}

/* ---------- Legacy Handing Picker (kept for non-swing openings if ever needed) ---------- */
export function HandingPicker({ value, onChange }) {
  return (
    <div
      data-testid="quote-handing-picker"
      className="grid grid-cols-2 gap-2"
    >
      {["Left Hand", "Right Hand"].map((h) => {
        const flip = h === "Right Hand";
        const selected = value === h;
        return (
          <button
            key={h}
            type="button"
            data-testid={`quote-handing-${h.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => onChange(h)}
            className={`flex items-center gap-3 rounded-sm border px-3 py-2 transition-all duration-300 ${
              selected
                ? "border-[#0a0a0c] bg-[#0a0a0c] text-white"
                : "border-[#0a0a0c]/20 bg-white text-[#0a0a0c]/80 hover:border-[#c6a87c]"
            }`}
          >
            <svg
              width="34"
              height="34"
              viewBox="0 0 40 40"
              aria-hidden
              style={flip ? { transform: "scaleX(-1)" } : undefined}
              className="flex-shrink-0"
            >
              <circle cx="6" cy="20" r="2" fill="currentColor" />
              <line x1="6" y1="4" x2="6" y2="36" stroke="currentColor" strokeWidth="1.5" />
              <path d="M 6 20 Q 22 6 34 20" stroke="currentColor" strokeWidth="1.5" fill="none" strokeDasharray="2 2" />
              <line x1="6" y1="20" x2="30" y2="10" stroke="currentColor" strokeWidth="2.5" />
              <path d="M 30 10 L 26 12 M 30 10 L 28 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
            <span className="font-tech text-[10px] font-semibold uppercase tracking-[0.28em]">{h}</span>
          </button>
        );
      })}
    </div>
  );
}
